import React, { useState, useEffect } from 'react';
import { Palette, Save, Upload, Loader2, Check, Globe, Type, Paintbrush, X } from 'lucide-react';
import { Button } from '../ui/button';
import { ImageWithAuth } from '../ImageWithAuth';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const getAuth = () => ({});

const PRESET_COLORS = [
  '#B49A88', '#E60023', '#111111', '#6B8F6B', '#D4A0A0', '#5B7BA5',
  '#E87461', '#8B5CF6', '#F59E0B', '#EC4899', '#14B8A6', '#1E1B2E',
];

const FONT_OPTIONS = [
  'Cormorant Garamond', 'Inter', 'Playfair Display', 'Lora', 'Montserrat',
  'Raleway', 'Open Sans', 'Merriweather', 'Poppins', 'Roboto Slab',
];

const BrandKitPanel = ({ brandKit, onUpdate }) => {
  const [form, setForm] = useState({
    brand_name: '',
    website: '',
    primary_color: '#B49A88',
    secondary_colors: [],
    font_primary: 'Inter',
    font_secondary: 'Cormorant Garamond',
    default_cta: 'Try it free',
    logo_file_id: null,
    watermark_file_id: null,
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [newColor, setNewColor] = useState('#B49A88');

  useEffect(() => {
    if (brandKit) {
      setForm({
        brand_name: brandKit.brand_name || '',
        website: brandKit.website || '',
        primary_color: brandKit.primary_color || '#B49A88',
        secondary_colors: brandKit.secondary_colors || [],
        font_primary: brandKit.font_primary || 'Inter',
        font_secondary: brandKit.font_secondary || 'Cormorant Garamond',
        default_cta: brandKit.default_cta || 'Try it free',
        logo_file_id: brandKit.logo_file_id || null,
        watermark_file_id: brandKit.watermark_file_id || null,
      });
    }
  }, [brandKit]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/api/brand-kit`, form, getAuth());
      onUpdate?.(res.data);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      console.error('Save brand kit error:', e);
    }
    setSaving(false);
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/api/storage/upload?folder=brand`, fd, {
        ...getAuth(),
        headers: { ...getAuth().headers, 'Content-Type': 'multipart/form-data' },
      });
      setForm((f) => ({ ...f, logo_file_id: res.data.file_id }));
    } catch (e) {
      alert('Opplasting feilet');
    }
    setUploading(false);
  };

  const addSecondaryColor = () => {
    if (form.secondary_colors.length < 6 && !form.secondary_colors.includes(newColor)) {
      setForm((f) => ({ ...f, secondary_colors: [...f.secondary_colors, newColor] }));
    }
  };

  const removeSecondaryColor = (idx) => {
    setForm((f) => ({ ...f, secondary_colors: f.secondary_colors.filter((_, i) => i !== idx) }));
  };

  return (
    <div className="space-y-6" data-testid="brand-kit-panel">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-serif font-bold text-stone-800 text-xl">Brand Kit</h2>
          <p className="font-sans text-xs text-stone-400 mt-0.5">Used automatically on Pinterest, Instagram, Etsy and all exports</p>
        </div>
        <Button
          data-testid="save-brand-kit-btn"
          onClick={handleSave}
          disabled={saving}
          className={`rounded-xl px-5 py-2.5 text-xs font-sans font-semibold transition-all ${
            saved ? 'bg-green-600 text-white' : 'bg-stone-900 hover:bg-stone-800 text-white'
          }`}
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : saved ? <Check className="w-3.5 h-3.5 mr-1.5" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
          {saved ? 'Saved' : 'Save'}
        </Button>
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        {/* Left column: Identity */}
        <div className="space-y-5">
          <div className="bg-white rounded-2xl border border-stone-100 p-5 space-y-4">
            <h3 className="font-sans font-bold text-stone-700 text-xs flex items-center gap-2"><Globe className="w-3.5 h-3.5 text-[#B49A88]" /> Identitet</h3>
            <div>
              <label className="font-sans text-[10px] font-semibold text-stone-500 mb-1 block">Brand Name</label>
              <input
                data-testid="brand-name-input"
                value={form.brand_name}
                onChange={(e) => setForm((f) => ({ ...f, brand_name: e.target.value }))}
                placeholder="Your brand name..."
                className="w-full px-3 py-2.5 border border-stone-200 rounded-xl text-sm font-sans text-stone-700 placeholder:text-stone-300 focus:outline-none focus:border-[#B49A88] transition-colors"
              />
            </div>
            <div>
              <label className="font-sans text-[10px] font-semibold text-stone-500 mb-1 block">Website</label>
              <input
                data-testid="brand-website-input"
                value={form.website}
                onChange={(e) => setForm((f) => ({ ...f, website: e.target.value }))}
                placeholder="https://..."
                className="w-full px-3 py-2.5 border border-stone-200 rounded-xl text-sm font-sans text-stone-700 placeholder:text-stone-300 focus:outline-none focus:border-[#B49A88] transition-colors"
              />
            </div>
            <div>
              <label className="font-sans text-[10px] font-semibold text-stone-500 mb-1 block">Standard CTA</label>
              <input
                data-testid="brand-cta-input"
                value={form.default_cta}
                onChange={(e) => setForm((f) => ({ ...f, default_cta: e.target.value }))}
                placeholder="e.g. Try it free, Shop now..."
                className="w-full px-3 py-2.5 border border-stone-200 rounded-xl text-sm font-sans text-stone-700 placeholder:text-stone-300 focus:outline-none focus:border-[#B49A88] transition-colors"
              />
            </div>

            {/* Logo */}
            <div>
              <label className="font-sans text-[10px] font-semibold text-stone-500 mb-1.5 block">Logo</label>
              <div className="flex items-center gap-3">
                {form.logo_file_id ? (
                  <div className="w-14 h-14 rounded-xl border border-stone-100 overflow-hidden">
                    <ImageWithAuth fileId={form.logo_file_id} alt="Logo" className="w-full h-full object-contain" />
                  </div>
                ) : (
                  <div className="w-14 h-14 rounded-xl border border-dashed border-stone-200 flex items-center justify-center">
                    <Palette className="w-5 h-5 text-stone-200" />
                  </div>
                )}
                <label className={`cursor-pointer px-3 py-2 rounded-xl border border-stone-200 text-xs font-sans font-medium text-stone-600 hover:bg-stone-50 transition-colors ${uploading ? 'opacity-50' : ''}`}>
                  {uploading ? <Loader2 className="w-3 h-3 animate-spin inline mr-1" /> : <Upload className="w-3 h-3 inline mr-1" />}
                  {form.logo_file_id ? 'Change' : 'Upload'}
                  <input type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} disabled={uploading} data-testid="logo-upload-input" />
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Right column: Colors & Fonts */}
        <div className="space-y-5">
          <div className="bg-white rounded-2xl border border-stone-100 p-5 space-y-4">
            <h3 className="font-sans font-bold text-stone-700 text-xs flex items-center gap-2"><Paintbrush className="w-3.5 h-3.5 text-[#B49A88]" /> Farger</h3>
            <div>
              <label className="font-sans text-[10px] font-semibold text-stone-500 mb-1.5 block">Primaryfarge</label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={form.primary_color}
                  onChange={(e) => setForm((f) => ({ ...f, primary_color: e.target.value }))}
                  className="w-9 h-9 rounded-lg border-0 cursor-pointer"
                  data-testid="primary-color-picker"
                />
                <input
                  value={form.primary_color}
                  onChange={(e) => setForm((f) => ({ ...f, primary_color: e.target.value }))}
                  className="w-24 px-2 py-1.5 border border-stone-200 rounded-lg text-xs font-mono text-stone-600"
                />
                <div className="flex gap-1 flex-wrap">
                  {PRESET_COLORS.slice(0, 6).map((c) => (
                    <button
                      key={c}
                      onClick={() => setForm((f) => ({ ...f, primary_color: c }))}
                      className="w-6 h-6 rounded-full border border-stone-200 hover:scale-110 transition-transform"
                      style={{ background: c }}
                    />
                  ))}
                </div>
              </div>
            </div>

            <div>
              <label className="font-sans text-[10px] font-semibold text-stone-500 mb-1.5 block">Secondaryfarger</label>
              <div className="flex items-center gap-2 flex-wrap">
                {form.secondary_colors.map((c, i) => (
                  <div key={i} className="relative group">
                    <div className="w-8 h-8 rounded-lg border border-stone-200" style={{ background: c }} />
                    <button
                      onClick={() => removeSecondaryColor(i)}
                      className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-2 h-2" />
                    </button>
                  </div>
                ))}
                {form.secondary_colors.length < 6 && (
                  <div className="flex items-center gap-1">
                    <input type="color" value={newColor} onChange={(e) => setNewColor(e.target.value)} className="w-8 h-8 rounded-lg border-0 cursor-pointer" />
                    <button onClick={addSecondaryColor} className="text-[10px] font-sans font-medium text-[#B49A88] hover:underline">+ Add</button>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-stone-100 p-5 space-y-4">
            <h3 className="font-sans font-bold text-stone-700 text-xs flex items-center gap-2"><Type className="w-3.5 h-3.5 text-[#B49A88]" /> Typografi</h3>
            <div>
              <label className="font-sans text-[10px] font-semibold text-stone-500 mb-1 block">Primary font (headings)</label>
              <select
                data-testid="font-primary-select"
                value={form.font_primary}
                onChange={(e) => setForm((f) => ({ ...f, font_primary: e.target.value }))}
                className="w-full px-3 py-2.5 border border-stone-200 rounded-xl text-sm font-sans text-stone-700 bg-white focus:outline-none focus:border-[#B49A88]"
              >
                {FONT_OPTIONS.map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
              <p className="mt-2 text-sm text-stone-400" style={{ fontFamily: form.font_primary }}>
                The quick brown fox jumps over the lazy dog
              </p>
            </div>
            <div>
              <label className="font-sans text-[10px] font-semibold text-stone-500 mb-1 block">Secondary font (body)</label>
              <select
                data-testid="font-secondary-select"
                value={form.font_secondary}
                onChange={(e) => setForm((f) => ({ ...f, font_secondary: e.target.value }))}
                className="w-full px-3 py-2.5 border border-stone-200 rounded-xl text-sm font-sans text-stone-700 bg-white focus:outline-none focus:border-[#B49A88]"
              >
                {FONT_OPTIONS.map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BrandKitPanel;
